import type { SVGProps } from "react"

import { cx } from "@uui/utils/cx"

/*
 * The four capability marks — one instrument, four positions.
 *
 * Canvas 0 0 16 16, one monoline at 1.7 with round caps, no fills. The wall is
 * an arc of a circle r5 driven by stroke-dasharray on pathLength=100; the
 * travelling arc is r2.6. Extent, seam bearing, place and rotation are the whole
 * vocabulary (docs/design/identity.md).
 *
 * A mark is a stroke on paper in its hue — 14–16px beside a number, 28px in an
 * alert, 72px on a sheet — never a filled tile. Pass tone="ink" where the strokes
 * must stay ink (on a capability fill, in a legend beside its label).
 */
export type Capability = "know" | "believe" | "say" | "do"

export const CAPABILITY_LABEL: Record<Capability, string> = {
  know: "Know",
  believe: "Believe",
  say: "Say",
  do: "Do",
}

const ARC = "M2.6 0A2.6 2.6 0 0 1 -2.6 0"

const GLYPH: Record<Capability, React.ReactNode> = {
  know: (
    <circle
      cx="8"
      cy="8"
      r="5"
      pathLength="100"
      strokeDasharray="83.33 16.67"
      strokeDashoffset="-8.333"
    />
  ),
  believe: (
    <>
      <circle
        cx="8"
        cy="8"
        r="5"
        pathLength="100"
        strokeDasharray="83.33 16.67"
        strokeDashoffset="-8.333"
      />
      <g transform="translate(8,7.2)">
        <path d={ARC} />
      </g>
    </>
  ),
  say: (
    <>
      <circle
        cx="8"
        cy="8"
        r="5"
        pathLength="100"
        strokeDasharray="61.11 38.89"
        strokeDashoffset="-11.11"
      />
      <g transform="translate(11.5,5.98) rotate(-120)">
        <path d={ARC} />
      </g>
    </>
  ),
  do: (
    <>
      <circle
        cx="8"
        cy="8"
        r="5"
        pathLength="100"
        strokeDasharray="50 50"
        strokeDashoffset="0"
      />
      <line x1="13" y1="8" x2="13" y2="5.2" />
      <line x1="3" y1="8" x2="3" y2="5.2" />
      <g transform="translate(8,3.9) rotate(-160)">
        <path d={ARC} />
      </g>
    </>
  ),
}

const HUE_CLASS: Record<Capability, string> = {
  know: "text-know-500",
  believe: "text-believe-500",
  say: "text-say-500",
  do: "text-do-500",
}

export function CapabilityMark({
  capability,
  size = 16,
  tone = "hue",
  className,
  ...props
}: {
  capability: Capability
  /** Rendered box in px: 14–16 beside a number, 28 in an alert, 72 on a sheet. */
  size?: number
  /** "hue" draws in the capability's 500 step; "ink" inherits currentColor. */
  tone?: "hue" | "ink"
} & Omit<SVGProps<SVGSVGElement>, "width" | "height">) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      role="img"
      aria-label={CAPABILITY_LABEL[capability]}
      className={cx(
        "shrink-0",
        tone === "hue" && HUE_CLASS[capability],
        className,
      )}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      {...props}
    >
      {GLYPH[capability]}
    </svg>
  )
}
